/**
 * Problema → Oportunidade.
 * A criação grava só a oportunidade. Não cria ação, Kaizen, projeto nem muda a issue.
 */
import type { Issue, IssueType } from '../types/issue';
import { ISSUE_TYPE_LABELS } from '../types/issue';
import type { Opportunity, OpportunityCreateInput, OpportunitySourceKind, OpportunityStatus } from '../types/opportunity';

const TERMINAL_OPPORTUNITY: ReadonlySet<OpportunityStatus> = new Set(['realized', 'dismissed']);

export const OPPORTUNITY_CREATE_EFFECTS = {
  createsAction: false,
  createsKaizen: false,
  changesIssueStatus: false,
  writesKaizenProblemId: false,
  writesIssueOpportunityId: false,
} as const;

export interface OpportunityInsert {
  organization_id: string;
  title: string;
  description: string | null;
  category: string;
  impact_level: OpportunityCreateInput['impact_level'];
  effort_level: OpportunityCreateInput['effort_level'];
  owner_id: string | null;
  source_kind: OpportunitySourceKind;
  status: 'captured';
  created_by: string | null;
  source_issue_id?: string;
  source_label?: string;
  source_context?: Record<string, unknown>;
}

export function buildOpportunityInsert(
  organizationId: string,
  input: OpportunityCreateInput,
  createdBy: string | null,
): OpportunityInsert {
  const sourceKind = input.source_kind ?? 'manual';
  const payload: OpportunityInsert = {
    organization_id: organizationId,
    title: input.title.trim(),
    description: input.description?.trim() || null,
    category: input.category?.trim() || 'other',
    impact_level: input.impact_level ?? null,
    effort_level: input.effort_level ?? null,
    owner_id: input.owner_id || null,
    source_kind: sourceKind,
    status: 'captured',
    created_by: createdBy,
  };

  if (sourceKind === 'problem') {
    const issueId = input.source_issue_id?.trim();
    if (!issueId) throw new Error('Informe o problema de origem.');
    payload.source_issue_id = issueId;
    const label = input.source_label?.trim();
    if (label) payload.source_label = label;
    if (input.source_context && Object.keys(input.source_context).length > 0) {
      payload.source_context = input.source_context;
    }
  }

  return payload;
}

export function canCreateOpportunityFromIssue(status: string): boolean {
  return status !== 'closed';
}

export function isOpenOpportunity(status: OpportunityStatus): boolean {
  return !TERMINAL_OPPORTUNITY.has(status);
}

export function openOpportunityCount(rows: Array<Pick<Opportunity, 'status'>>): number {
  return rows.filter((row) => isOpenOpportunity(row.status)).length;
}

export function openOpportunityNotice(count: number): string | null {
  if (count <= 0) return null;
  if (count === 1) return 'Este problema já possui 1 oportunidade de melhoria.';
  return `Este problema já possui ${count} oportunidades de melhoria.`;
}

/** Aviso não bloqueia. Várias oportunidades do mesmo problema são válidas. */
export function anotherOpportunityAllowed(existingOpenCount: number): boolean {
  return existingOpenCount >= 0;
}

export function opportunitiesForIssue<T extends { source_kind: string; source_issue_id: string | null }>(
  rows: T[],
  issueId: string,
): T[] {
  return rows.filter((row) => row.source_kind === 'problem' && row.source_issue_id === issueId);
}

export function problemSourceLabel(issue: Pick<Issue, 'title' | 'type'>): string {
  const typeLabel = ISSUE_TYPE_LABELS[issue.type as IssueType] ?? 'Problema';
  const title = issue.title.trim() || 'Sem título';
  return `Problema: ${typeLabel} — ${title}`;
}

export function problemSourceContext(issue: Pick<Issue, 'id' | 'title' | 'type' | 'severity' | 'process' | 'step'>): Record<string, string> {
  const context: Record<string, string> = {
    issue_id: issue.id,
    issue_title: issue.title,
    issue_type: issue.type,
    severity: issue.severity,
  };
  const process = issue.process?.trim();
  const step = issue.step?.trim();
  if (process) context.process = process;
  if (step) context.step = step;
  return context;
}

export function problemOpportunityDraft(issue: Issue): Pick<
  OpportunityCreateInput,
  'title' | 'description' | 'source_kind' | 'source_issue_id' | 'source_label' | 'source_context'
> {
  return {
    title: issue.title,
    description: issue.description?.trim() || '',
    source_kind: 'problem',
    source_issue_id: issue.id,
    source_label: problemSourceLabel(issue),
    source_context: problemSourceContext(issue),
  };
}

/** Desdobramento da oportunidade. Não recebe actions.problem_id. */
export function buildOpportunityActionOutcome(
  opportunityId: string,
  actionId: string,
  createdBy: string | null,
): { opportunity_id: string; action_id: string; created_by: string | null } {
  return {
    opportunity_id: opportunityId,
    action_id: actionId,
    created_by: createdBy,
  };
}
